import type { GraphQLFormattedError } from "graphql";
import Link from "next/link";
import { useEffect } from "react";
import {
  Button,
  Center,
  Container,
  H2,
  Stack,
  Text,
} from "@/components/ui/elements";
import { logError } from "@/libs/loging/slack";

import { replaceLocation } from "./_function/replaceLocation";
import { MaintenanceError } from "./Maintenance";

type Props = {
  graphQLErrors: ReadonlyArray<GraphQLFormattedError>;
  reset: () => void;
  notFoundHandling?: {
    text: string;
    link: string;
    // true の場合、ボタン表示を待たずに link へ自動で遷移する
    autoRedirect?: boolean;
  };
};
export function GraphqlErrors({
  graphQLErrors,
  reset,
  notFoundHandling,
}: Props) {
  const isUnAuthorized = graphQLErrors.some(
    (e) => e.extensions?.code === "UNAUTHORIZED_ERROR",
  );
  const isNotFound = graphQLErrors.some(
    (e) => e.extensions?.code === "NOT_FOUND_ERROR",
  );
  const isForbidden = graphQLErrors.some(
    (e) => e.extensions?.code === "FORBIDDEN_ERROR",
  );
  const isPreconditionFailed = graphQLErrors.some(
    (e) => e.extensions?.code === "PRECONDITION_FAILED_ERROR",
  );
  const isServerError = graphQLErrors.some(
    (e) => e.extensions?.code === "SERVER_ERROR",
  );
  const isUnProcessableContent = graphQLErrors.some(
    (e) => e.extensions?.code === "UNPROCESSABLE_CONTENT_ERROR",
  );
  const isMaintenance = graphQLErrors.some(
    (e) => e.extensions?.code === "SERVICE_UNAVAILABLE",
  );
  const errorMessage = graphQLErrors?.[0]?.message || "エラーメッセージなし";
  useEffect(() => {
    if (isUnAuthorized) return;
    if (graphQLErrors.length === 0) return;
    logError({
      errorHandling: "graphqlErrors",
      message: errorMessage,
      type: (graphQLErrors?.[0]?.extensions?.code as string) || "エラー名なし",
      url: window.location.href,
      userAgent: window.navigator.userAgent,
      code: (graphQLErrors?.[0]?.extensions?.code as string) || "エラー名なし",
      stack: JSON.stringify(graphQLErrors) || "スタックトレースなし",
    });
  }, [graphQLErrors, isUnAuthorized, errorMessage]);

  // 描画中に副作用を走らせると再描画のたびにタイマーが増え、エラー境界からの
  // soft navigation で Application error になることがあるため useEffect + ハード遷移にする
  useEffect(() => {
    if (!isUnAuthorized) return;
    document.cookie = "auth_token=; max-age=0; path=/;";
    const timer = window.setTimeout(() => {
      window.location.replace("/signup");
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [isUnAuthorized]);

  const notFoundRedirectLink =
    isNotFound && notFoundHandling?.autoRedirect
      ? notFoundHandling.link
      : undefined;
  // エラー境界からの soft navigation は Application error になることがあるため、ハード遷移にする
  useEffect(() => {
    if (!notFoundRedirectLink) return;
    replaceLocation(notFoundRedirectLink);
  }, [notFoundRedirectLink]);

  if (isMaintenance) {
    return <MaintenanceError />;
  }
  if (isUnAuthorized) {
    return (
      <Container fluid bg="gray.0" mih={"100dvh"}>
        <Center w={"100%"} h={"100dvh"}>
          <Stack>
            <H2>再ログインが必要です。</H2>
            <Text>
              サインアップ画面へリダイレクトしています。しばらくお待ちください。
              <br />
              リダイレクトされない場合はログイン画面へ戻るから再度ログインしてください。
            </Text>
            <Button component={Link} href={"/signup"}>
              ログイン画面へ戻る
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }
  if (isNotFound) {
    if (notFoundHandling) {
      const { link, text } = notFoundHandling;
      return (
        <Container fluid bg="gray.0" mih={"100dvh"}>
          <Center w={"100%"} h={"100dvh"}>
            <Stack>
              <H2>{errorMessage}</H2>
              <Button component={Link} href={link}>
                {text}
              </Button>
            </Stack>
          </Center>
        </Container>
      );
    }
    return (
      <Container fluid bg="gray.0" mih={"100dvh"}>
        <Center w={"100%"} h={"100dvh"}>
          <Stack>
            <H2>{errorMessage}</H2>
            <Button component={Link} href={"/work"}>
              求人一覧へ戻る
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }
  return (
    <Container fluid bg="gray.0" mih={"100dvh"}>
      <Center w={"100%"} h={"100dvh"}>
        <Stack>
          <H2>データ取得中にエラーが発生しました。</H2>
          <Text>
            時間をおいて再試行してください。解決しない場合は管理者にお問い合わせください。
            <br />
            以下のエラーが発生しています。
          </Text>
          {graphQLErrors.map((e, i) => (
            <Text key={i}>
              エラーメッセージ：{e.message || "エラーメッセージなし"}
            </Text>
          ))}
          <Button
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.reload();
              }
            }}
          >
            リセット
          </Button>
          <Button component={Link} href={"/signup"}>
            ログイン画面へ戻る
          </Button>
        </Stack>
      </Center>
    </Container>
  );
}
